import React from 'react';
import { Icon, useBoolean, useDisclosure } from '@wegroup/design-system';
import CompanySuccessActionBox from './CompanySuccessActionBox';
import useSessionId from '../../../../flows/src/hooks/useSessionId';
import { useTranslation } from 'react-i18next';
import useTracking from '../../../../common/src/hooks/tracking/useTracking';
import { usePushNewAdvisoryReport } from '../../../../advisory-report/src/api/createAdvisoryReport';
import ContinueReportModal from '../../../../advisory-report/src/features/detail/components/header/actions/continue-report-modal/ContinueReportModal';
import { useAdvisoryReportsByParty } from '../../../../advisory-report/src/features/overview/api/getAdvisoryReports';
import AdvisoryReportLanguageModal from '@risk-analysis/src/pages/recommendations/AdvisoryReportLanguageModal';
import { Language } from '../../../../../types/Common';

interface Props {
  partyId?: string;
  recommendationId?: string;
}

const CompanySuccessNewAdvisoryReportActionBox: React.FC<Props> = ({
  partyId,
  recommendationId,
}) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLanguageModalOpen,
    onOpen: onOpenLanguage,
    onClose: onCloseLanguage,
  } = useDisclosure();
  const conversationId = useSessionId();
  const [isLoading, setIsLoading] = useBoolean();
  const { trackEvent } = useTracking();
  const { data: advisoryReports, isLoading: isLoadingAdvisoryReports } =
    useAdvisoryReportsByParty(partyId);
  const latestAdvisoryReport = advisoryReports?.items?.[0];

  const createNewAdvisortReportForParty = usePushNewAdvisoryReport(partyId);

  const handleNewAdvisoryReport = async (language?: Language) => {
    setIsLoading.on();
    trackEvent('Create Advisory Report from Company Scan');
    await createNewAdvisortReportForParty(
      partyId,
      recommendationId,
      conversationId,
      language,
    );
    setIsLoading.off();
  };

  const handleClick = () => {
    if (latestAdvisoryReport && latestAdvisoryReport.status !== 'ACCEPTED') {
      return onOpen();
    }
    onOpenLanguage();
  };

  return (
    <>
      {isLanguageModalOpen && (
        <AdvisoryReportLanguageModal
          isOpen={isLanguageModalOpen}
          onClose={onCloseLanguage}
          onLanguageSelected={(language) => handleNewAdvisoryReport(language)}
        />
      )}
      {latestAdvisoryReport && latestAdvisoryReport.status !== 'ACCEPTED' && (
        <ContinueReportModal
          isOpen={isOpen}
          onClose={onClose}
          reportId={latestAdvisoryReport.id}
          partyId={partyId || ''}
        />
      )}
      <CompanySuccessActionBox
        icon={<Icon name="GeneralNavigationNewAdvisoryReport" />}
        title={t('COMPANY-SCAN_RECOMMENDATIONS_CREATE-ADVISORY-REPORT_TITLE')}
        description={t(
          'COMPANY-SCAN_RECOMMENDATIONS_CREATE-ADVISORY-REPORT_DESCRIPTION',
        )}
        onClick={handleClick}
        isLoading={isLoading || isLoadingAdvisoryReports}
        data-test-id="COMPANY-SCAN_RECOMMENDATIONS_CREATE-ADVISORY-REPORT_BUTTON"
      />
    </>
  );
};

export default CompanySuccessNewAdvisoryReportActionBox;
